import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import { QRCodeSVG } from "qrcode.react";
import {
  ANDROID_STORE_URL,
  APP_SCHEME,
  IOS_STORE_URL,
} from "../utility/constants";
import { getMobilePlatform } from "../utility/deviceUtils";

const PRIMARY = "#046287";
const PRIMARY_HOVER = "#035070";

const storeButtonSx = {
  borderColor: PRIMARY,
  color: PRIMARY,
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 700,
  flex: 1,
};

const StoreButton = ({ platform }) =>
  platform === "ios" ? (
    <Button
      component="a"
      href={IOS_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      startIcon={<AppleIcon />}
      sx={storeButtonSx}
    >
      App Store
    </Button>
  ) : (
    <Button
      component="a"
      href={ANDROID_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      startIcon={<AndroidIcon />}
      sx={storeButtonSx}
    >
      Google Play
    </Button>
  );

// The https equivalent of a custom-scheme deep link, e.g.
// "ngwaahia://user/settings/set-password" -> "https://ngwaahia.com/user/settings/set-password".
// Scanned on a phone, a universal-link path opens the app (or the website when
// the app isn't installed); the bare root lands on the store-download page.
const toWebLink = (deepLink) => {
  const path = deepLink.replace(/^[^:]+:\/\//, "");
  return `${window.location.origin}/${path}`;
};

/**
 * Generic "this feature lives in the app" modal.
 *
 * On a phone it offers an "Open Ngwaahia App" button (custom-scheme deep link)
 * plus that platform's store link. On desktop, where no app can handle the
 * scheme, it shows a QR code for the same destination plus both store links.
 *
 * Props:
 *  open       – boolean
 *  onClose    – () => void
 *  title      – string  (e.g. "Sign up in the app")
 *  body       – string  (e.g. "Creating an account is done in the Ngwaahia app…")
 *  deepLink   – string  (e.g. `${APP_SCHEME}://signup`)  defaults to `${APP_SCHEME}://`
 *  footer     – node    optional content rendered below the store links
 */
const AppRedirectModal = ({
  open,
  onClose,
  title = "Continue in the app",
  body = "This feature is available in the Ngwaahia app.",
  deepLink = `${APP_SCHEME}://`,
  footer,
}) => {
  const platform = getMobilePlatform();

  const handleOpenApp = () => {
    window.location.href = deepLink;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={700} fontSize={18}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {body}
        </Typography>

        {platform ? (
          <>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleOpenApp}
              endIcon={<OpenInNewIcon />}
              sx={{
                bgcolor: PRIMARY,
                "&:hover": { bgcolor: PRIMARY_HOVER },
                borderRadius: 2,
                mb: 2,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Open Ngwaahia App
            </Button>

            <Box
              sx={{
                p: 2,
                bgcolor: "#f8fafc",
                borderRadius: 2,
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
                sx={{ mb: 1 }}
              >
                Don't have the app yet?
              </Typography>
              <Box sx={{ display: "flex" }}>
                <StoreButton platform={platform} />
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
                mb: 2,
                bgcolor: "#f8fafc",
                borderRadius: 2,
                border: "1px solid #e5e7eb",
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "#ffffff",
                  borderRadius: 1,
                  lineHeight: 0,
                }}
              >
                <QRCodeSVG
                  value={toWebLink(deepLink)}
                  size={168}
                  fgColor="#111827"
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 1.5 }}
              >
                Scan with your phone's camera to continue in the Ngwaahia app.
              </Typography>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              Don't have the app yet?
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <StoreButton platform="ios" />
              <StoreButton platform="android" />
            </Box>
          </>
        )}
        {footer && <Box sx={{ mt: 2.5 }}>{footer}</Box>}
      </DialogContent>
    </Dialog>
  );
};

export default AppRedirectModal;
